import type { DocumentMeta, Rect } from '../core/types';

export interface PixelSnapshot {
  rect: Rect;
  data: Uint8Array;
}

export interface FullSnapshot {
  width: number;
  height: number;
  /** `null` means "a freshly cleared surface", which avoids storing blank megabytes. */
  data: Uint8Array | null;
  /** Clear colour used when `data` is null; masks need opaque white. */
  fill?: readonly [number, number, number, number];
}

export function blankSnapshot(
  width: number,
  height: number,
  fill?: readonly [number, number, number, number],
): FullSnapshot {
  return { width, height, data: null, fill };
}

/**
 * Undo is snapshot-based: each op remembers the bytes before and after the
 * change. Region snapshots keep brush strokes cheap; full snapshots are only
 * used by operations that replace a whole surface (resize, layer add/remove).
 */
export type EngineHistoryOp =
  | {
      kind: 'pixels';
      layerId: string;
      before: PixelSnapshot;
      after: PixelSnapshot;
      bytes: number;
    }
  | {
      kind: 'surface';
      layerId: string;
      before: FullSnapshot | null;
      after: FullSnapshot | null;
      bytes: number;
    }
  | {
      kind: 'layerMask';
      layerId: string;
      before: FullSnapshot | null;
      after: FullSnapshot | null;
      bytes: number;
    }
  | {
      kind: 'maskPixels';
      layerId: string;
      before: PixelSnapshot;
      after: PixelSnapshot;
      bytes: number;
    }
  | {
      kind: 'channel';
      channelId: string;
      before: FullSnapshot | null;
      after: FullSnapshot | null;
      bytes: number;
    }
  | {
      kind: 'channelPixels';
      channelId: string;
      before: PixelSnapshot;
      after: PixelSnapshot;
      bytes: number;
    }
  | {
      kind: 'selection';
      before: FullSnapshot | null;
      after: FullSnapshot | null;
      bytes: number;
    }
  | {
      kind: 'meta';
      before: DocumentMeta;
      after: DocumentMeta;
      bytes: number;
    };

export function pixelOp(
  layerId: string,
  before: PixelSnapshot,
  after: PixelSnapshot,
): EngineHistoryOp {
  return {
    kind: 'pixels',
    layerId,
    before,
    after,
    bytes: before.data.byteLength + after.data.byteLength,
  };
}

/** Region op for a paint stroke that landed on a layer mask instead of pixels. */
export function maskPixelOp(
  layerId: string,
  before: PixelSnapshot,
  after: PixelSnapshot,
): EngineHistoryOp {
  return {
    kind: 'maskPixels',
    layerId,
    before,
    after,
    bytes: before.data.byteLength + after.data.byteLength,
  };
}

function snapshotBytes(...snapshots: (FullSnapshot | null)[]): number {
  return snapshots.reduce((sum, snapshot) => sum + (snapshot?.data?.byteLength ?? 0), 0);
}

export function surfaceOp(
  layerId: string,
  before: FullSnapshot | null,
  after: FullSnapshot | null,
): EngineHistoryOp {
  return { kind: 'surface', layerId, before, after, bytes: snapshotBytes(before, after) };
}

/** Region op for a stroke that landed in an alpha channel. */
export function channelPixelOp(
  channelId: string,
  before: PixelSnapshot,
  after: PixelSnapshot,
): EngineHistoryOp {
  return {
    kind: 'channelPixels',
    channelId,
    before,
    after,
    bytes: before.data.byteLength + after.data.byteLength,
  };
}

/** Whole-channel op: add, delete or replace the contents of an alpha channel. */
export function channelOp(
  channelId: string,
  before: FullSnapshot | null,
  after: FullSnapshot | null,
): EngineHistoryOp {
  return { kind: 'channel', channelId, before, after, bytes: snapshotBytes(before, after) };
}

export function layerMaskOp(
  layerId: string,
  before: FullSnapshot | null,
  after: FullSnapshot | null,
): EngineHistoryOp {
  return { kind: 'layerMask', layerId, before, after, bytes: snapshotBytes(before, after) };
}

export function selectionOp(
  before: FullSnapshot | null,
  after: FullSnapshot | null,
): EngineHistoryOp {
  return { kind: 'selection', before, after, bytes: snapshotBytes(before, after) };
}

export function metaOp(before: DocumentMeta, after: DocumentMeta): EngineHistoryOp {
  // Metadata is tiny; a nominal cost keeps it from being evicted on its own.
  return { kind: 'meta', before, after, bytes: 512 };
}
